//--------------------------------------------------------------------//
//******************** SipTrunksService ********************************//
//--------------------------------------------------------------------//
// API communication layer for SIP trunk CRUD, backed by sip-gudhub-server's
// /sip-trunks REST API (docs: sip-gudhub-server/docs/sip-trunks). app_id is
// never sent — the server doesn't actually store or require it despite the
// docs listing it (SipTrunksDatabase.createTrunk only reads name/host/etc).
import { request } from './sipTrunks.request.js';
import { GUDHUB_SETTINGS } from '../../../config.js';
require('../../../core/localizationService/localizationService.js');

// Shared by testCall and testRegister below - both stream newline-delimited
// JSON while a debug action is still in progress, not a single object, so
// neither can go through request()'s plain `await response.json()`. Same
// raw fetch()+getReader()+TextDecoder pattern already used for NDJSON
// progress streaming in gh_elements/install_app_action.js. onEvent(event)
// fires once per line, in arrival order - the caller (trunk-debug.tab.js)
// is responsible for its own $scope.$applyAsync() after each one, same as
// every other async update in this app.
async function streamNdjsonPost(path, body, onEvent, failureMessage) {
    const url = `${GUDHUB_SETTINGS.sip_server_url}${path}`;
    const token = await gudhub.getToken();

    const response = await fetch(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify(body)
    });

    if (!response.ok) {
        // Preflight failure (bad auth/404/403/400) - the backend only sends
        // a plain JSON body like every other route when this happens, since
        // it's always before the first NDJSON line is written. Same error
        // contract as request()'s own.
        const errorBody = await response.json().catch(() => ({}));
        throw new Error(errorBody.message || failureMessage);
    }

    const reader = response.body.getReader();
    const decoder = new TextDecoder();
    let buffer = '';

    while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split('\n');
        buffer = lines.pop();

        for (const line of lines) {
            if (!line.trim()) continue;
            onEvent(JSON.parse(line));
        }
    }

    if (buffer.trim()) {
        onEvent(JSON.parse(buffer));
    }
}

// Shared by the voicemail-greeting methods below - that endpoint needs a raw
// audio/wav body and a binary response, neither of which request() supports
// (it always sends Content-Type: application/json and always awaits
// response.json()). Same URL/auth-header construction as request() and
// streamNdjsonPost above, just without forcing a Content-Type or parsing the
// response - callers do that themselves since it differs per verb.
async function fetchWithAuth(path, options = {}) {
    const url = `${GUDHUB_SETTINGS.sip_server_url}${path}`;
    const token = await gudhub.getToken();

    return fetch(url, {
        ...options,
        headers: { ...(options.headers || {}), Authorization: `Bearer ${token}` }
    });
}

angular.module('sipTrunksTableModule', ['localizationModule'])

    .service('SipTrunksService', [function () {
        // Fetches all configured trunks.
        this.getList = function () {
            return request('/sip-trunks');
        };

        // Creates a new trunk. { name, host, provider?, username?, password?,
        // register?, caller_id_number?, did_numbers?, enabled? }
        this.create = function (trunkData) {
            return request('/sip-trunks', {
                method: 'POST',
                body: angular.toJson(trunkData)
            });
        };

        // Updates a trunk (partial update — only send changed fields).
        // Goes through angular.toJson (not plain JSON.stringify) because
        // trunkData's nested objects (e.g. did_numbers entries) can be the
        // same objects an ng-repeat elsewhere in the trunk popup is bound to
        // without `track by`, which taints them with a live $$hashKey the
        // server rejects as an invalid $-prefixed field.
        this.update = function (trunkId, trunkData) {
            return request(`/sip-trunks/${encodeURIComponent(trunkId)}`, {
                method: 'PUT',
                body: angular.toJson(trunkData)
            });
        };

        // Deletes a trunk by its trunk_id.
        this.delete = function (trunkId) {
            return request(`/sip-trunks/${encodeURIComponent(trunkId)}`, {
                method: 'DELETE'
            });
        };

        // Places a one-off outbound call through the trunk to a provider-
        // required short code (e.g. Zadarma's "call 8888 to verify your IP").
        // Resolves to { success, result } even when the call itself failed
        // (result is FreeSWITCH's raw +OK/-ERR text) - it only rejects on a
        // request-level error (bad auth, disabled trunk, invalid number).
        this.sendShortCode = function (trunkId, number) {
            return request(`/sip-trunks/${encodeURIComponent(trunkId)}/send-short-code`, {
                method: 'POST',
                body: JSON.stringify({ number })
            });
        };

        // Reveals a trunk's real, unmasked sms.credentials - the only call
        // that ever returns them after they've been saved (GET /sip-trunks
        // and GET /sip-trunks/:id always mask sms.credentials). See
        // trunk-sms.tab.js's ensureRealCredential.
        this.getSmsCredentials = function (trunkId) {
            return request(`/sip-trunks/${encodeURIComponent(trunkId)}/sms/credentials`);
        };

        // Places a real test call through the trunk and streams back the raw
        // FreeSWITCH SIP trace as it happens - see
        // sip-gudhub-server/docs/sip-trunks/debug-tab.md.
        // { from, to } - from is optional (server falls back to the trunk's
        // own caller_id_number, then to no override at all - see
        // sip-gudhub-server/docs/sip-trunks/debug-tab.md); to is required.
        this.testCall = function (trunkId, { from, to }, onEvent) {
            const body = { to };
            if (from) body.from = from;

            return streamNdjsonPost(
                `/sip-trunks/${encodeURIComponent(trunkId)}/debug/test-call`,
                body,
                onEvent,
                'Test call request failed'
            );
        };

        // Forces a fresh REGISTER attempt on the trunk's own gateway and
        // streams the exchange the same NDJSON way testCall does - see
        // sip-gudhub-server/docs/sip-trunks/debug-tab.md. Only meaningful for
        // a trunk with register: true; the server 400s otherwise. No { from,
        // to } - there's no per-call destination, this exercises the
        // gateway's own registration, not a call through it.
        this.testRegister = function (trunkId, onEvent) {
            return streamNdjsonPost(
                `/sip-trunks/${encodeURIComponent(trunkId)}/debug/test-register`,
                {},
                onEvent,
                'Test register request failed'
            );
        };

        // Uploads (or replaces) a DID's custom voicemail greeting - see
        // sip-gudhub-server/docs/sip-trunks/voice-mail-greeting-client-guide.md.
        // wavBlob must already be an audio/wav Blob (the server does no
        // transcoding). Resolves { file_id, url }.
        this.uploadVoicemailGreeting = async function (trunkId, number, wavBlob) {
            const response = await fetchWithAuth(
                `/sip-trunks/${encodeURIComponent(trunkId)}/did-numbers/${encodeURIComponent(number)}/voice-mail-greeting`,
                { method: 'PUT', headers: { 'Content-Type': 'audio/wav' }, body: wavBlob }
            );

            const result = await response.json();

            if (!response.ok) {
                throw new Error(result.message || 'Failed to save voicemail greeting');
            }

            return result;
        };

        // Fetches a DID's recorded greeting as a playable Blob, or null if
        // none is recorded yet (server 404s rather than erroring for that
        // case specifically).
        this.getVoicemailGreetingBlob = async function (trunkId, number) {
            const response = await fetchWithAuth(
                `/sip-trunks/${encodeURIComponent(trunkId)}/did-numbers/${encodeURIComponent(number)}/voice-mail-greeting`
            );

            if (response.status === 404) {
                return null;
            }

            if (!response.ok) {
                const result = await response.json().catch(() => ({}));
                throw new Error(result.message || 'Failed to load voicemail greeting');
            }

            return response.blob();
        };

        // Deletes a DID's recorded greeting. Resolves on the server's 204.
        this.deleteVoicemailGreeting = async function (trunkId, number) {
            const response = await fetchWithAuth(
                `/sip-trunks/${encodeURIComponent(trunkId)}/did-numbers/${encodeURIComponent(number)}/voice-mail-greeting`,
                { method: 'DELETE' }
            );

            if (response.status !== 204) {
                const result = await response.json().catch(() => ({}));
                throw new Error(result.message || 'Failed to delete voicemail greeting');
            }
        };
    }]);
